import { AIR, CHUNK_SIZE, CHUNK_VOLUME, MESH_BOUNDARY } from '../constants';
import { fnv1a } from '../math';
import { BinaryReader, BinaryWriter } from '../net/codec';
import { MaterialTable } from './materials';
import type { MeshFamilies } from './meshFamilies';
import type { PlayArea } from './playArea';

export const PAD = CHUNK_SIZE + 2;
export const PAD_AREA = PAD * PAD;
export const PAD_VOLUME = PAD * PAD * PAD;

export class Chunk {
  /** Voxel palette indices, or null when the whole chunk is `fill`. Layout: x | z << 5 | y << 10. */
  data: Uint8Array | null = null;
  fill = AIR;
  /** Accumulated partial damage per local voxel index. */
  dmg: Map<number, number> | null = null;
  /** Incremented on every voxel change. */
  rev = 0;
  /** Incremented on every content change, partial damage included (caches of the serialized chunk). */
  ver = 0;
  /** `data` is shared with another world (copy-on-write): copy before the first write. */
  shared = false;
}

/** Visual crack stage 0..3 for accumulated damage. */
export function damageLevel(dmg: number, hp: number): number {
  if (dmg <= 0 || hp <= 0) return 0;
  return Math.min(3, Math.ceil((dmg / hp) * 3));
}

export function localIndex(lx: number, ly: number, lz: number): number {
  return lx | (lz << 5) | (ly << 10);
}

/**
 * Chunked voxel grid. Coordinates are integer voxel indices; y is up.
 * Horizontal out-of-bounds space and everything below y=0 is solid for collision
 * but reads as air for rendering and raycasts.
 */
export class VoxelWorld {
  readonly ncx: number;
  readonly ncy: number;
  readonly ncz: number;
  readonly chunks: Chunk[];
  /** Chunk indices whose meshes need rebuilding. Consumers delete entries they handle. */
  readonly dirty = new Set<number>();
  /** 1 when a chunk's voxels or damage differ from the generated map. */
  readonly modified: Uint8Array;
  /** When false, writes skip dirty/modified bookkeeping (used during generation). */
  track = true;
  /** When set, receives (x + z * sx) for every changed voxel column. */
  columnLog: number[] | null = null;
  /** Columns players may be in (an invisible wall at every height round it, for movement only); null = the whole world. Set once, when the map is generated. */
  playArea: PlayArea | null = null;
  /** Per-column terrain materials from `heightfield` (rendering only, like the families): r-channel = surface material, g-channel = sub-column material, 2 bytes per column (x + z * sx); null when the map has no column terrain. The voxel shader reads it for family modes 2/3 so fbm-painted lawns merge and still render exactly as authored. */
  surf: Uint8Array | null = null;
  /** Brush families of this map (speckle/stripes material sets the mesher may merge across, the shader re-picking the member per voxel); null = none. Rendering only; the voxels and the world hash are unchanged. */
  meshFamilies: MeshFamilies | null = null;

  constructor(
    readonly sx: number,
    readonly sy: number,
    readonly sz: number,
    readonly mats: MaterialTable,
  ) {
    if (sx % CHUNK_SIZE || sy % CHUNK_SIZE || sz % CHUNK_SIZE) {
      throw new Error(`World size must be a multiple of ${CHUNK_SIZE}: ${sx}x${sy}x${sz}`);
    }
    this.ncx = sx / CHUNK_SIZE;
    this.ncy = sy / CHUNK_SIZE;
    this.ncz = sz / CHUNK_SIZE;
    const n = this.ncx * this.ncy * this.ncz;
    this.chunks = new Array(n);
    for (let i = 0; i < n; i++) this.chunks[i] = new Chunk();
    this.modified = new Uint8Array(n);
  }

  get chunkCount(): number {
    return this.chunks.length;
  }

  chunkIndex(cx: number, cy: number, cz: number): number {
    return cx + this.ncx * (cz + this.ncz * cy);
  }

  chunkCoords(ci: number): [number, number, number] {
    const cx = ci % this.ncx;
    const rest = (ci - cx) / this.ncx;
    const cz = rest % this.ncz;
    const cy = (rest - cz) / this.ncz;
    return [cx, cy, cz];
  }

  inBounds(x: number, y: number, z: number): boolean {
    return x >= 0 && y >= 0 && z >= 0 && x < this.sx && y < this.sy && z < this.sz;
  }

  get(x: number, y: number, z: number): number {
    if (x < 0 || y < 0 || z < 0 || x >= this.sx || y >= this.sy || z >= this.sz) return AIR;
    const c = this.chunks[(x >> 5) + this.ncx * ((z >> 5) + this.ncz * (y >> 5))];
    const d = c.data;
    return d === null ? c.fill : d[(x & 31) | ((z & 31) << 5) | ((y & 31) << 10)];
  }

  /** Collision query: map sides and floor are solid walls, the sky is open. */
  isSolid(x: number, y: number, z: number): boolean {
    if (y < 0) return true;
    if (x < 0 || z < 0 || x >= this.sx || z >= this.sz) return true;
    if (y >= this.sy) return false;
    const c = this.chunks[(x >> 5) + this.ncx * ((z >> 5) + this.ncz * (y >> 5))];
    const d = c.data;
    return (d === null ? c.fill : d[(x & 31) | ((z & 31) << 5) | ((y & 31) << 10)]) !== AIR;
  }

  /** Sets a voxel and returns its previous value. */
  set(x: number, y: number, z: number, v: number): number {
    if (x < 0 || y < 0 || z < 0 || x >= this.sx || y >= this.sy || z >= this.sz) return AIR;
    const ci = (x >> 5) + this.ncx * ((z >> 5) + this.ncz * (y >> 5));
    const c = this.chunks[ci];
    let d = c.data;
    if (d === null) {
      if (c.fill === v) return v;
      d = c.data = new Uint8Array(CHUNK_VOLUME);
      d.fill(c.fill);
    }
    const li = (x & 31) | ((z & 31) << 5) | ((y & 31) << 10);
    const old = d[li];
    if (old === v) return old;
    if (c.shared) {
      d = c.data = d.slice();
      c.shared = false;
    }
    d[li] = v;
    if (c.dmg !== null) {
      c.dmg.delete(li);
      if (c.dmg.size === 0) c.dmg = null;
    }
    c.rev++;
    c.ver++;
    if (this.track) this.touch(x, y, z, ci);
    return old;
  }

  private touch(x: number, y: number, z: number, ci: number): void {
    this.dirty.add(ci);
    this.modified[ci] = 1;
    if (this.columnLog !== null) this.columnLog.push(x + z * this.sx);
    const lx = x & 31;
    const ly = y & 31;
    const lz = z & 31;
    const ox = lx === 0 ? -1 : lx === 31 ? 1 : 0;
    const oy = ly === 0 ? -1 : ly === 31 ? 1 : 0;
    const oz = lz === 0 ? -1 : lz === 31 ? 1 : 0;
    if ((ox | oy | oz) === 0) return;
    const cx = x >> 5;
    const cy = y >> 5;
    const cz = z >> 5;
    for (let a = 0; a <= (ox !== 0 ? 1 : 0); a++) {
      for (let b = 0; b <= (oy !== 0 ? 1 : 0); b++) {
        for (let e = 0; e <= (oz !== 0 ? 1 : 0); e++) {
          if (a === 0 && b === 0 && e === 0) continue;
          const nx = cx + a * ox;
          const ny = cy + b * oy;
          const nz = cz + e * oz;
          if (nx < 0 || ny < 0 || nz < 0 || nx >= this.ncx || ny >= this.ncy || nz >= this.ncz) continue;
          this.dirty.add(this.chunkIndex(nx, ny, nz));
        }
      }
    }
  }

  getDamage(x: number, y: number, z: number): number {
    if (!this.inBounds(x, y, z)) return 0;
    const c = this.chunks[(x >> 5) + this.ncx * ((z >> 5) + this.ncz * (y >> 5))];
    if (c.dmg === null) return 0;
    return c.dmg.get((x & 31) | ((z & 31) << 5) | ((y & 31) << 10)) ?? 0;
  }

  /**
   * Adds integer damage to a voxel. Returns the palette index of the voxel if it broke, else AIR.
   * Indestructible voxels (hp 0) ignore damage.
   */
  damage(x: number, y: number, z: number, amount: number): number {
    if (amount <= 0 || !this.inBounds(x, y, z)) return AIR;
    const ci = (x >> 5) + this.ncx * ((z >> 5) + this.ncz * (y >> 5));
    const c = this.chunks[ci];
    const li = (x & 31) | ((z & 31) << 5) | ((y & 31) << 10);
    const v = c.data === null ? c.fill : c.data[li];
    if (v === AIR) return AIR;
    const hp = this.mats.hp[v];
    if (hp === 0) return AIR;
    const prev = c.dmg === null ? 0 : (c.dmg.get(li) ?? 0);
    const total = prev + amount;
    if (total >= hp) {
      this.set(x, y, z, AIR);
      return v;
    }
    if (c.dmg === null) c.dmg = new Map();
    const prevLevel = damageLevel(prev, hp);
    c.dmg.set(li, total);
    c.ver++;
    if (this.track) {
      this.modified[ci] = 1;
      // Crack stages are visible, so remesh when a voxel crosses into a new stage.
      if (damageLevel(total, hp) !== prevLevel) {
        c.rev++;
        this.dirty.add(ci);
      }
    }
    return AIR;
  }

  /** Fills the half-open box [x0,x1) x [y0,y1) x [z0,z1), clipped to the world. */
  fillBox(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, v: number): void {
    x0 = Math.max(0, Math.floor(x0));
    y0 = Math.max(0, Math.floor(y0));
    z0 = Math.max(0, Math.floor(z0));
    x1 = Math.min(this.sx, Math.floor(x1));
    y1 = Math.min(this.sy, Math.floor(y1));
    z1 = Math.min(this.sz, Math.floor(z1));
    if (x0 >= x1 || y0 >= y1 || z0 >= z1) return;
    if (this.track) {
      for (let y = y0; y < y1; y++) for (let z = z0; z < z1; z++) for (let x = x0; x < x1; x++) this.set(x, y, z, v);
      return;
    }
    for (let cy = y0 >> 5; cy <= (y1 - 1) >> 5; cy++) {
      for (let cz = z0 >> 5; cz <= (z1 - 1) >> 5; cz++) {
        for (let cx = x0 >> 5; cx <= (x1 - 1) >> 5; cx++) {
          const c = this.chunks[this.chunkIndex(cx, cy, cz)];
          const bx0 = Math.max(x0, cx << 5) - (cx << 5);
          const bx1 = Math.min(x1, (cx + 1) << 5) - (cx << 5);
          const by0 = Math.max(y0, cy << 5) - (cy << 5);
          const by1 = Math.min(y1, (cy + 1) << 5) - (cy << 5);
          const bz0 = Math.max(z0, cz << 5) - (cz << 5);
          const bz1 = Math.min(z1, (cz + 1) << 5) - (cz << 5);
          c.rev++;
          c.ver++;
          c.dmg = null;
          if (bx0 === 0 && by0 === 0 && bz0 === 0 && bx1 === 32 && by1 === 32 && bz1 === 32) {
            c.data = null;
            c.fill = v;
            continue;
          }
          let d = c.data;
          if (d === null) {
            if (c.fill === v) continue;
            d = c.data = new Uint8Array(CHUNK_VOLUME);
            d.fill(c.fill);
          } else if (c.shared) {
            d = c.data = d.slice();
            c.shared = false;
          }
          for (let ly = by0; ly < by1; ly++) {
            for (let lz = bz0; lz < bz1; lz++) {
              const row = (lz << 5) | (ly << 10);
              d.fill(v, row + bx0, row + bx1);
            }
          }
        }
      }
    }
  }

  /**
   * Copy-on-write clone: chunk arrays are shared until either world writes to a chunk.
   * Used to start many matches from one generated map without regenerating or copying it.
   */
  cloneShared(): VoxelWorld {
    const w = new VoxelWorld(this.sx, this.sy, this.sz, this.mats);
    w.playArea = this.playArea;
    w.meshFamilies = this.meshFamilies;
    w.surf = this.surf;
    for (let i = 0; i < this.chunks.length; i++) {
      const src = this.chunks[i];
      const dst = w.chunks[i];
      dst.fill = src.fill;
      if (src.data !== null) {
        dst.data = src.data;
        dst.shared = true;
        src.shared = true;
      }
      if (src.dmg !== null) dst.dmg = new Map(src.dmg);
    }
    return w;
  }

  /** Converts chunks whose arrays hold a single value back to the compact uniform form. */
  compact(): void {
    for (const c of this.chunks) {
      const d = c.data;
      if (d === null || c.dmg !== null) continue;
      const v = d[0];
      let uniform = true;
      for (let i = 1; i < CHUNK_VOLUME; i++) {
        if (d[i] !== v) {
          uniform = false;
          break;
        }
      }
      if (uniform) {
        c.data = null;
        c.fill = v;
      }
    }
  }

  /** Call after procedural generation: compacts storage and resets change tracking. */
  finishGeneration(): void {
    this.compact();
    this.dirty.clear();
    this.modified.fill(0);
    this.track = true;
  }

  /** True if the chunk is uniform. */
  isUniform(ci: number): boolean {
    return this.chunks[ci].data === null;
  }

  /** Highest solid voxel y in a column, or -1. */
  topSolid(x: number, z: number): number {
    for (let y = this.sy - 1; y >= 0; y--) if (this.get(x, y, z) !== AIR) return y;
    return -1;
  }

  /** Read used by meshing: outside the map reads as MESH_BOUNDARY so boundary faces are culled. */
  getForMesh(x: number, y: number, z: number): number {
    if (y < 0 || x < 0 || z < 0 || x >= this.sx || z >= this.sz) return MESH_BOUNDARY;
    if (y >= this.sy) return AIR;
    return this.get(x, y, z);
  }

  /**
   * Copies a chunk plus a one-voxel border into a (34^3) array laid out as
   * px + pz * 34 + py * 34 * 34. Used by the mesher.
   */
  copyPadded(ci: number, out: Uint8Array, dmgOut?: Uint8Array): boolean {
    const [cx, cy, cz] = this.chunkCoords(ci);
    const bx = cx << 5;
    const by = cy << 5;
    const bz = cz << 5;
    const c = this.chunks[ci];
    for (let py = 0; py < PAD; py++) {
      const edgeY = py === 0 || py === PAD - 1;
      for (let pz = 0; pz < PAD; pz++) {
        const edgeZ = pz === 0 || pz === PAD - 1;
        const rowBase = pz * PAD + py * PAD_AREA;
        if (!edgeY && !edgeZ) {
          // Interior row: copy 32 voxels straight from the chunk.
          if (c.data === null) {
            out.fill(c.fill, rowBase + 1, rowBase + 33);
          } else {
            const src = ((pz - 1) << 5) | ((py - 1) << 10);
            out.set(c.data.subarray(src, src + 32), rowBase + 1);
          }
          out[rowBase] = this.getForMesh(bx - 1, by + py - 1, bz + pz - 1);
          out[rowBase + PAD - 1] = this.getForMesh(bx + 32, by + py - 1, bz + pz - 1);
        } else {
          for (let px = 0; px < PAD; px++) {
            out[rowBase + px] = this.getForMesh(bx + px - 1, by + py - 1, bz + pz - 1);
          }
        }
      }
    }
    // Damage stages for this chunk's own voxels (faces belong to their voxel).
    if (!dmgOut || c.dmg === null) return false;
    dmgOut.fill(0);
    const d = c.data;
    for (const [li, dmg] of c.dmg) {
      const v = d === null ? c.fill : d[li];
      const level = damageLevel(dmg, this.mats.hp[v]);
      if (level === 0) continue;
      const lx = li & 31;
      const lz = (li >> 5) & 31;
      const ly = li >> 10;
      dmgOut[lx + 1 + (lz + 1) * PAD + (ly + 1) * PAD_AREA] = level;
    }
    return true;
  }

  /** Writes a chunk's voxels as canonical run-length pairs (value, varint length). */
  writeChunkVoxels(ci: number, w: BinaryWriter): void {
    const c = this.chunks[ci];
    if (c.data === null) {
      w.u8(c.fill).varint(CHUNK_VOLUME);
      return;
    }
    const d = c.data;
    let run = d[0];
    let len = 1;
    for (let i = 1; i < CHUNK_VOLUME; i++) {
      const v = d[i];
      if (v === run) {
        len++;
      } else {
        w.u8(run).varint(len);
        run = v;
        len = 1;
      }
    }
    w.u8(run).varint(len);
  }

  /** Full chunk state (voxels + partial damage) for late-join sync. */
  writeChunk(ci: number, w: BinaryWriter): void {
    this.writeChunkVoxels(ci, w);
    const dmg = this.chunks[ci].dmg;
    if (dmg === null) {
      w.varint(0);
      return;
    }
    const keys = [...dmg.keys()].sort((a, b) => a - b);
    w.varint(keys.length);
    for (const k of keys) w.varint(k).u8(Math.min(255, dmg.get(k)!));
  }

  readChunk(ci: number, r: BinaryReader): void {
    const c = this.chunks[ci];
    let first = -1;
    let i = 0;
    let d: Uint8Array | null = null;
    while (i < CHUNK_VOLUME) {
      const v = r.u8();
      const len = r.varint();
      if (first === -1 && len === CHUNK_VOLUME) {
        first = v;
        i = CHUNK_VOLUME;
        break;
      }
      if (d === null) d = new Uint8Array(CHUNK_VOLUME);
      d.fill(v, i, i + len);
      i += len;
    }
    if (d === null) {
      c.data = null;
      c.fill = first;
    } else {
      c.data = d;
    }
    c.shared = false;
    const n = r.varint();
    c.dmg = null;
    for (let k = 0; k < n; k++) {
      const li = r.varint();
      const amount = r.u8();
      if (c.dmg === null) c.dmg = new Map();
      c.dmg.set(li, amount);
    }
    c.rev++;
    c.ver++;
    this.modified[ci] = 1;
    // Neighbors need remeshing too since border faces may change.
    const [cx, cy, cz] = this.chunkCoords(ci);
    for (let dy = -1; dy <= 1; dy++)
      for (let dz = -1; dz <= 1; dz++)
        for (let dx = -1; dx <= 1; dx++) {
          const nx = cx + dx;
          const ny = cy + dy;
          const nz = cz + dz;
          if (nx < 0 || ny < 0 || nz < 0 || nx >= this.ncx || ny >= this.ncy || nz >= this.ncz) continue;
          this.dirty.add(this.chunkIndex(nx, ny, nz));
        }
  }

  private hashWriter = new BinaryWriter(4096);

  /** Representation-independent hash of a chunk's voxels. */
  hashChunk(ci: number): number {
    const w = this.hashWriter.reset();
    this.writeChunkVoxels(ci, w);
    return fnv1a(w.view8());
  }

  /** Hash of the whole world (voxels only). */
  hashWorld(): number {
    let h = 0x811c9dc5;
    for (let ci = 0; ci < this.chunks.length; ci++) {
      h = Math.imul(h ^ this.hashChunk(ci), 0x01000193) >>> 0;
    }
    return h >>> 0;
  }

  /** Approximate heap bytes used by voxel storage. */
  memoryBytes(): number {
    let n = 0;
    for (const c of this.chunks) if (c.data) n += CHUNK_VOLUME;
    return n;
  }
}
